import { register } from 'register-service-worker'
import store from './store/index'
import { IStore } from './core/interface/IStore'
import { ISysUpdateModule } from '@/contracts/system-modules/update/ISysUpdateModule'
import { ISysConsoleModule } from '@/contracts/system-modules/console/ISysConsoleModule'
import { EnumConsoleType } from '@/contracts/system-modules/console/enum/EnumConsoleType'
import { SysConsoleProviderModule } from '@/system-modules/console/ConsoleProvider'

export const UPDATE_CHECK_INTERVAL_MS = 30000
/** Сколько нажатие «Обновить» ждёт скачивания найденной новой версии */
export const INSTALL_WAIT_MS = 60000

export class SysUpdateV1 implements ISysUpdateModule {
  private static instance: ISysUpdateModule
  private store: IStore = store()
  private console: ISysConsoleModule
  private registration: ServiceWorkerRegistration | null = null
  private checking = false
  private reloading = false
  private reload: () => void

  constructor(
    console: ISysConsoleModule = SysConsoleProviderModule(),
    reload: () => void = () => window.location.reload(),
  ) {
    this.console = console
    this.reload = reload
  }

  public static getInstance(): ISysUpdateModule {
    if (!SysUpdateV1.instance) {
      SysUpdateV1.instance = new SysUpdateV1()
    }
    return SysUpdateV1.instance
  }

  // вне production и без service worker модуль ничего не делает: обновление — обычной перезагрузкой
  init(): boolean {
    if (process.env.NODE_ENV !== 'production' || !('serviceWorker' in navigator)) {
      return true
    }

    register(`${process.env.BASE_URL}service-worker.js`, {
      registered: (registration) => {
        this.registration = registration
        // новая версия могла быть скачана в прошлый раз, а кнопку не нажали
        if (registration.waiting) {
          this.store.available = true
        }
        void this.check()
      },
      updated: (registration) => {
        this.registration = registration
        this.store.available = true
      },
      ready: () => this.console.log('App is served by a service worker'),
      cached: () => this.console.log('Content is cached for offline use'),
      offline: () => this.console.log('No connection: app is running from cache', EnumConsoleType.WARNING),
      error: (error) => this.console.log(`Service worker registration failed: ${error}`, EnumConsoleType.ERROR),
    })

    setInterval(() => void this.check(), UPDATE_CHECK_INTERVAL_MS)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void this.check()
      }
    })

    return true
  }

  async check(): Promise<void> {
    if (!this.registration || !navigator.onLine || this.checking) {
      return
    }

    this.checking = true
    try {
      await this.registration.update()
    } catch {
      // сеть пропала по ходу проверки: следующая проверка повторит
    } finally {
      this.checking = false
    }
  }

  isUpdateAvailable(): boolean {
    return this.store.available
  }

  // controllerchange слушается только после нажатия: смена воркера другой вкладкой эту не перезагружает
  applyUpdate(): void {
    if (this.reloading) {
      return
    }
    this.reloading = true

    const waiting = this.registration?.waiting
    if (!waiting) {
      this.reload()
      return
    }

    navigator.serviceWorker.addEventListener('controllerchange', () => this.reload(), { once: true })
    waiting.postMessage({ type: 'SKIP_WAITING' })
  }

  // сразу после выкладки проверка находит воркер, который ещё скачивает оболочку: без ожидания
  // перезагрузка снова показала бы старую версию из кеша старого воркера
  async updateNow(): Promise<void> {
    if (this.store.updating) {
      return
    }
    this.store.updating = true
    try {
      await this.check()
      await this.waitForInstalling()
    } finally {
      this.applyUpdate()
      this.store.updating = false
    }
  }

  isUpdating(): boolean {
    return this.store.updating
  }

  /** Скачанный воркер становится ждущим (installed), оборванный — redundant; дольше предела не ждём */
  private waitForInstalling(): Promise<void> {
    const installing = this.registration?.installing
    if (!installing) {
      return Promise.resolve()
    }

    return new Promise((resolve) => {
      const done = () => {
        clearTimeout(timer)
        installing.removeEventListener('statechange', onChange)
        resolve()
      }
      const onChange = () => {
        if (installing.state !== 'installing') {
          done()
        }
      }
      const timer = setTimeout(done, INSTALL_WAIT_MS)
      installing.addEventListener('statechange', onChange)
    })
  }

  getViewPath(): string {
    return '/update/v1/view/index.vue'
  }
}

export default function(): ISysUpdateModule {
  return SysUpdateV1.getInstance()
}
